import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { InterviewService } from '../../services/interview.service';
import { ProfileInterviewSession, ReportSummary } from '../../models/virtual-interview-model';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs';

@Component({
  selector: 'app-interview-result',
  templateUrl: './interview-result.component.html',
  styleUrls: ['./interview-result.component.css']
})
export class InterviewResultComponent implements OnInit {
  isLoading: boolean = true;
  sessionId: string = '';
  attemptIndex: number = 0;
  pdfUrl: string = '';
  isProfileRoute: boolean = false;
  isCompleted: boolean = true;
  language: string = 'en';

  get isRTL(): boolean {
      return this.language?.toLowerCase() === 'ar';
  }

  candidateInfo = {
    identifier: '',
    position: ''
  };

  executiveSummary = {
    overallScore: 0,
    recommendation: '',
    duration: ''
  };

  performanceBreakdown = [
    {
      title: 'Communication',
      titleAr: 'التواصل',
      description: '',
      score: 0,
      icon: '../../../../../assets/img/Vector.svg',
      iconBgColor: '#CCA40033',
      scoreColor: '#CCA400'
    },
    {
      title: 'Role-Specific Knowledge',
      titleAr: 'المعرفة الوظيفية',
      description: '',
      score: 0,
      icon: '../../../../../assets/img/Group.svg',
      iconBgColor: '#F9865033',
      scoreColor: '#F98650'
    },
    {
      title: 'Problem Solving',
      titleAr: 'حل المشكلات',
      description: '',
      score: 0,
      icon: '../../../../../assets/img/Vector (1).svg',
      iconBgColor: '#8DB00033',
      scoreColor: '#8DB000'
    },
    {
      title: 'Confidence',
      titleAr: 'الثقة بالنفس',
      description: '',
      score: 0,
      icon: '../../../../../assets/img/Group (1).svg',
      iconBgColor: '#00B01533',
      scoreColor: '#00B015'
    }
  ];

  questions: { text: string; answer: string }[] = [];

  aiInsights = {
    keyStrengths: '',
    keyConcerns: '',
    overallAssessment: ''
  };

  // Static text translations
  staticText = {
    en: {
      reportGenerating: 'Your Report is being generated!',
      reportGeneratingDesc: 'Our AI is currently analyzing your performance.',
      checkBack: 'Please check back in about 2 hours.',
      backToProfile: 'Back to Profile',
      question: 'Question',
      keyStrengths: 'Key Strengths',
      keyConcerns: 'Key Concerns',
      overallAssessment: 'Overall Assessment',
      downloadPdf: 'Download as PDF',
      iterviewResult_title:"Welcome to your Interview Result.",
  iterviewResult_Candidate:"Candidate Identifier",
   iterviewResult_Position:"Position",
   iterviewResult_Executive:"Executive Summary",
   iterviewResult_Score:"Overall Interview Score",
   iterviewResult_Recommendation:"AI Recommendation",
   iterviewResult_Duration:"Interview Duration",
   iterviewResult_Performance:"Performance Breakdown",
   iterviewResult_Question:"Question Review",
   iterviewResult_Insights:"AI Insights"
    },
    ar: {
      reportGenerating: 'جارٍ إنشاء تقريرك!',
      reportGeneratingDesc: 'يقوم الذكاء الاصطناعي حاليًا بتحليل أدائك.',
      checkBack: 'يرجى المراجعة مرة أخرى بعد حوالي ساعتين.',
      backToProfile: 'العودة إلى الملف الشخصي',
      question: 'السؤال',
      keyStrengths: 'نقاط القوة الرئيسية',
      keyConcerns: 'المخاوف الرئيسية',
      overallAssessment: 'التقييم الشامل',
      downloadPdf: 'تحميل كـ PDF',
        iterviewResult_title:"أهلاً بك في صفحة نتائج المقابلة.",
      iterviewResult_Candidate:"معرّف المرشح",
   iterviewResult_Position:"موضع",
   iterviewResult_Executive:"ملخص تنفيذي",
   iterviewResult_Score:"مجموع نقاط المقابلة",
      iterviewResult_Recommendation:"توصيات الذكاء الاصطناعي"  ,
   iterviewResult_Duration:"مدة المقابلة",
   iterviewResult_Performance:"تفاصيل الأداء",
   iterviewResult_Question:"مراجعة السؤال",
   iterviewResult_Insights:"رؤى الذكاء الاصطناعي"
    }
  };

  get t() {
    return this.isRTL ? this.staticText.ar : this.staticText.en;
  }

  constructor(
    private router: Router,
    private interviewService: InterviewService
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      this.sessionId = navigation.extras.state['sessionId'];
      this.attemptIndex = navigation.extras.state['attemptIndex'] || 0;
    }
  }

  ngOnInit() {
    this.isProfileRoute = this.router.url.includes('/profile');

    if (!this.sessionId) {
      const state = history.state;
      if (state && state.sessionId) {
        this.sessionId = state.sessionId;
        this.attemptIndex = state.attemptIndex || 0;
      }
    }

    if (this.sessionId) {
      this.loadInterviewResults();
    } else {
      this.isLoading = false;
    }
  }

loadInterviewResults() {
  this.isLoading = true;

  if (this.isProfileRoute) {
    this.interviewService.getProfileInterviews().subscribe({
      next: (sessions: ProfileInterviewSession[]) => {
        const session = sessions.find(s => s.sessionId === this.sessionId);

        if (!session) { this.isLoading = false; return; }

        this.isCompleted = session.sessionStatus === 'COMPLETED';

        if (this.isCompleted && session.reportSummary) {
          this.populateResultData(session.reportSummary, session.jobTitle || '');
        }
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  } else {
    this.interviewService.getTrainingInterview().subscribe({
      next: (response) => {
        if (!response?.interviewProfileInfo) { this.isLoading = false; return; }

        const session = response.interviewProfileInfo.sessionsInfo?.find(
          s => s.sessionId === this.sessionId
        );

        if (!session) { this.isLoading = false; return; }

        this.isCompleted = session.sessionStatus === 'COMPLETED';
        this.language = ((session as any).language || 'en').toLowerCase();

        if (this.isCompleted && session.reportSummary) {
          this.populateResultData(session.reportSummary, response.interviewProfileInfo!.jobTitle);
        }
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }
}

  populateResultData(reportSummary: ReportSummary, jobTitle: string) {
    this.candidateInfo = {
      identifier: this.sessionId,
      position: reportSummary.position || jobTitle || 'Not specified'
    };

    this.executiveSummary = {
      overallScore: reportSummary.overallScore || 0,
      recommendation: reportSummary.aiRecommendation || 'Not available',
      duration: reportSummary.interviewDate
        ? new Date(reportSummary.interviewDate).toLocaleDateString()
        : 'Not available'
    };

    this.performanceBreakdown[0].score = reportSummary.communicationScore || 0;
    this.performanceBreakdown[0].description = reportSummary.communicationFeedback ||
      (this.isRTL
        ? 'يعبّر عن الأفكار بوضوح وإيجاز، مع الحفاظ على أسلوب حواري فعّال.'
        : 'Articulates ideas clearly and concisely, maintaining a conversational style while effectively conveying complex ideas.');

    this.performanceBreakdown[1].score = reportSummary.roleKnowledgeScore || 0;
    this.performanceBreakdown[1].description = reportSummary.roleKnowledgeFeedback ||
      (this.isRTL
        ? 'يُظهر خبرة شاملة في المفاهيم والممارسات ذات الصلة بالدور الوظيفي.'
        : 'Demonstrates comprehensive expertise in domain concepts and practices relevant to the role.');

    this.performanceBreakdown[2].score = reportSummary.problemSolvingScore || 0;
    this.performanceBreakdown[2].description = reportSummary.problemSolvingFeedback ||
      (this.isRTL
        ? 'يتعامل مع التحديات بمنهجية، مستخدمًا أساليب متنوعة وأفكاراً إبداعية.'
        : 'Approaches challenges methodically, utilizing diverse, offers fresh, originality and addressing roadblocks.');

    this.performanceBreakdown[3].score = reportSummary.confidenceScore || 0;
    this.performanceBreakdown[3].description = reportSummary.confidenceFeedback ||
      (this.isRTL
        ? 'يحافظ على مستوى ثابت من الثقة والاحترافية طوال المقابلة.'
        : 'Maintains a consistent level of confidence and professionalism throughout the interview.');

    this.questions = reportSummary.questionFeedbacks.map(q => ({
      text: q.questionText,
      answer: q.feedback
    }));

    this.aiInsights = {
      keyStrengths: reportSummary.keyStrengths || 'Not available',
      keyConcerns: reportSummary.keyConcerns || 'Not available',
      overallAssessment: reportSummary.overallFitSummary || 'Not available'
    };

    if (reportSummary.url) {
      let fixedUrl = reportSummary.url;
      if (fixedUrl.startsWith('/api/')) {
        fixedUrl = fixedUrl.substring(5);
      } else if (fixedUrl.startsWith('/')) {
        fixedUrl = fixedUrl.substring(1);
      }
      this.pdfUrl = environment.apiUrl + fixedUrl;
    }
  }

  getPerformanceTitle(item: any): string {
    return this.isRTL ? item.titleAr : item.title;
  }

  getRecommendationColor(recommendation: string): string {
    if (recommendation === 'Strong Hire') return '#3b82f6';
    if (recommendation === 'Hire') return '#22c55e';
    if (recommendation === 'Maybe') return '#f59e0b';
    return '#ef4444';
  }

  getCircumference(): number {
    const radius = 22;
    return 2 * Math.PI * radius;
  }

  getStrokeDashoffset(score: number): number {
    const circumference = this.getCircumference();
    const progress = score / 100;
    return circumference - (progress * circumference);
  }

  downloadPDF() {
    if (this.executiveSummary.recommendation === 'Not available') return;
    if (!this.pdfUrl) return;
    window.open(this.pdfUrl, '_blank');
  }
}